import { OperationTransforms } from '../../ot-slate-v2';
import type {
  AddAnnotationOperation,
  AnnotationsProvider,
  EditorPlugin,
  RemoveAnnotationOperation,
  SetAnnotationOperation,
} from '../../types/aha-editor';
import AhaEditor from '../../AhaEditor';
import Annotations from './Annotations';
import History from '../../collaboration/History';
import {
  type SetSelectionOperation,
  type Editor,
  Range,
  Transforms,
} from 'slate';
import RenderingUtils from '../../AhaEditorUtils/RenderingUtils';

export function handleAddAnnotation(
  editor: Editor,
  op: AddAnnotationOperation
) {
  History.withoutUndoSaving(editor, () => {
    OperationTransforms.AddAnnotation(editor, op);
  });

  // Only the metadata is affected by the operation above, but the change
  // likely changed the visual presentation of the editor, so we need to
  // force decorations to recompute and trigger a re-render.
  RenderingUtils.forceComputeDecorations(editor, true);

  return false;
}

export function handleRemoveAnnotation(
  editor: Editor,
  op: RemoveAnnotationOperation
) {
  if (op.type !== 'remove_annotation') return false;

  History.withoutUndoSaving(editor, () => {
    OperationTransforms.RemoveAnnotation(editor, op);
  });

  RenderingUtils.forceComputeDecorations(editor, true);

  return false;
}

export function handleSelectAnnotation(
  editor: Editor,
  op: SetSelectionOperation
) {
  if (op.type !== 'set_selection') return false;

  const ahaEditor = AhaEditor.getForEditor(editor);
  if (!ahaEditor) return false;

  // These handlers run before the apply, which means that
  // editor.selection is not up to date at this point.
  const newSelection = op.newProperties;
  if (!newSelection || !Range.isRange(newSelection)) return false;

  const ids = Annotations.getAnnotationIdsForHighlighting(
    editor,
    newSelection,
    {
      resolved: false,
    }
  );

  if (ids.length) {
    AhaEditor.withProvider(
      editor,
      'annotations',
      (provider: AnnotationsProvider) => {
        provider.onAnnotationsSelected(ids);
      }
    );
  }

  return false;
}

export function handleSetAnnotation(
  editor: Editor,
  op: SetAnnotationOperation
) {
  History.withoutUndoSaving(editor, () => {
    OperationTransforms.SetAnnotation(editor, op);
  });

  RenderingUtils.forceComputeDecorations(editor, true);

  return false;
}

let hookedDelete = false;

/* RangeRef transforms on annotations aren't enough to properly
 * restore annotation ranges after a delete then undo. Instead, we need
 * to snapshot accurate ranges that can be properly restored.
 *
 * There isn't a good way to do this in Slate, so we need to hook the
 * delete transform instead.
 */
function HookDeleteForSnapshotting(ahaEditor: AhaEditor): void {
  if (hookedDelete) return;
  hookedDelete = true;

  const previousDelete = Transforms.delete;

  Transforms.delete = (editor, options = {}) => {
    if (editor.metadata?.annotations) {
      Annotations.snapshotAnnotations(editor);
    }
    return previousDelete(editor, options);
  };
}

const AnnotationsOperationsHandler: EditorPlugin = {
  name: 'AnnotationsPlugin',
  handlers: {
    onInit: HookDeleteForSnapshotting,
  },
  operationHandlers: [
    {
      operation: 'add_annotation',
      run: handleAddAnnotation,
    },
    {
      operation: 'remove_annotation',
      run: handleRemoveAnnotation,
    },
    {
      operation: 'set_selection',
      run: handleSelectAnnotation,
    },
    {
      operation: 'set_annotation',
      run: handleSetAnnotation,
    },
  ],
};
export default AnnotationsOperationsHandler;
